import { ClassicRatePlanDetails } from "@/components/Inputs/RatesPicker/components/ClassicRatePlanDetails.tsx";
import { RatePlanDetailsProps } from "@/components/Inputs/RatesPicker/components/RatePlanDetailsProps.ts";
import { calculateNights } from "@/helpers/calculateNights.ts";
import { customRenderingPPNOrganizations } from "@/helpers/customFeatures.ts";
import { formatCurrency } from "@/helpers/formatCurrency.ts";
import { formatDiscount } from "@/helpers/formatDiscount.ts";
import { RatePlanMerged } from "@/helpers/interfaces/RatePlanMerged.ts";
import { RateSellMode } from "@/helpers/types/types.ts";
import { useGetOrganization } from "@/helpers/useGetOrganization.ts";
import { useEngineStore } from "@/stores/EngineStore/EngineStore.ts";
import { RootAttribute } from "@/stores/EngineStore/enums/RootAttribute.ts";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";

export const RatePlanDetails = (props: RatePlanDetailsProps) => {
  const { t } = useTranslation();
  const { organizationName } = useGetOrganization();
  const rootAttributes = useEngineStore((state) => state.rootAttributes);
  const shouldRenderClassicView = rootAttributes?.[RootAttribute.UseClassicRatePlanDetails];

  const reservationDays = useMemo(() => {
    const searchParams = new URLSearchParams(window.location.search);
    const startDate = searchParams.get("startDate") || useEngineStore.getState().defaultDateRange?.[0];
    const endDate = searchParams.get("endDate") || useEngineStore.getState().defaultDateRange?.[1];

    if (!startDate || !endDate) return 0;

    const start = new Date(startDate);
    const end = new Date(endDate);
    if (!start || !end) return 0;

    return calculateNights(start, end);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [window.location.search]);

  const isLongerThan1Day = reservationDays > 1;

  const showDiscount = useMemo(() => {
    return props.ratePlan.defaultPriceBreakdown.discountPercentage > 0;
  }, [props.ratePlan.defaultPriceBreakdown?.discountPercentage]);

  const RP: RatePlanMerged = useMemo(() => {
    return props.ratePlan.rateSellMode !== RateSellMode.PerPerson
      ? (props.ratePlan as RatePlanMerged)
      : (props.ratePlan.childRatePlans?.find((rp) => rp.occupancy === 2) as RatePlanMerged) ||
          (props.ratePlan as RatePlanMerged);
  }, [props.ratePlan]);

  if (!props.ratePlan.isAvailable) return null;
  if (shouldRenderClassicView) return <ClassicRatePlanDetails {...props} />;

  const getOccupancyText = () => {
    if (RP?.rateGroup?.guestCapacity === 1 && RP?.occupancy === 1) {
      return `${RP.occupancy} ${t("person")}`;
    }

    return `2 ${t("persons")}`;
  };

  return (
    <div className="c-rate__center u-h6 u-fw-600">
      {showDiscount && (
        <span className="c-rate__center--discount">
          {t("saveWithDiscount")} {formatDiscount(RP.defaultPriceBreakdown.discountPercentage)}
        </span>
      )}
      <div className="c-rate__center-price">
        {showDiscount ? (
          <span className="c-rate__center--discount-original-price">
            {formatCurrency(RP.defaultPriceBreakdown.withoutDiscount)}
          </span>
        ) : (
          <span className="c-rate__from">{t("from")} </span>
        )}
        {formatCurrency(RP.defaultPriceBreakdown?.withDiscount || 0)}
      </div>
      {/* Custom (no)rendering */}
      {customRenderingPPNOrganizations.includes(organizationName || "") || (
        <>
          <div className="c-rate__info u-p1">
            {`${getOccupancyText()}, ${reservationDays} ${isLongerThan1Day ? t("nights") : t("night")}`}
          </div>
        </>
      )}
    </div>
  );
};
